import React, { useCallback, useRef, useState } from "react";
import { api } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Upload, Pencil, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import WorkerFormDialog from "@/pages/manager/WorkerFormDialog";

const emptyForm = { name: "", employee_code: "", email: "", pin: "" };

function WorkersTable({ workers, onEdit, onDelete, onResetPin }) {
  if (workers.length === 0) {
    return <p className="p-10 text-center text-slate-500">No employees yet.</p>;
  }
  return (
    <table className="w-full">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Name</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Employee ID</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Email</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">PIN login</th>
          <th className="text-right text-xs uppercase tracking-wider text-slate-500 p-4 w-44">Actions</th>
        </tr>
      </thead>
      <tbody>
        {workers.map((w) => (
          <tr key={w.id} className="border-b last:border-0">
            <td className="p-4 text-sm font-medium text-slate-900">{w.name}</td>
            <td className="p-4 text-sm text-slate-700 font-mono">{w.employee_code || "—"}</td>
            <td className="p-4 text-sm text-slate-700">{w.email || "—"}</td>
            <td className="p-4 text-sm text-slate-700">{w.mobile_login_enabled ? "Enabled" : "Not set"}</td>
            <td className="p-4 text-right">
              <button onClick={() => onResetPin(w)} className="text-slate-600 hover:text-slate-900 p-2" title="Set / reset PIN">
                <KeyRound className="h-4 w-4" />
              </button>
              <button onClick={() => onEdit(w)} className="text-slate-600 hover:text-slate-900 p-2" data-testid={`employee-edit-${w.id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => onDelete(w)} className="text-red-600 hover:text-red-800 p-2" data-testid={`employee-delete-${w.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function Workers() {
  const fetcher = useCallback(() => api.get("/workers").then((r) => r.data), []);
  const { data, loading, reload } = useFetch(fetcher);
  const { data: status } = useFetch(useCallback(() => api.get("/account/status").then((r) => r.data), []));
  const workers = data || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const fileRef = useRef(null);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({ name: w.name, employee_code: w.employee_code || "", email: w.email || "", pin: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.pin) delete payload.pin;
      if (editing) {
        await api.put(`/workers/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/workers", payload);
        toast.success(payload.pin ? "Employee added with PIN login" : "Employee added");
      }
      setOpen(false);
      reload();
    } catch (err) {
      console.error("Employee save failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const resetPin = async (w) => {
    if (!window.confirm(`Reset PIN for "${w.name}"?`)) return;
    try {
      const { data } = await api.post(`/workers/${w.id}/reset-pin`);
      toast.success(`New mobile PIN for ${w.name}: ${data.temporary_pin}`);
      reload();
    } catch (err) {
      console.error("Employee PIN reset failed:", err);
      toast.error(err?.response?.data?.detail || "PIN reset failed");
    }
  };

  const del = async (w) => {
    if (!window.confirm(`Delete employee "${w.name}"? This also removes their mobile login.`)) return;
    try {
      await api.delete(`/workers/${w.id}`);
      toast.success("Employee deleted");
      reload();
    } catch (err) {
      console.error("Employee delete failed:", err);
      toast.error("Delete failed");
    }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data: result } = await api.post("/workers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${result.created} employees`);
      reload();
    } catch (err) {
      console.error("Employee import failed:", err);
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
  };

  let body;
  if (loading) body = <p className="p-6 text-slate-500">Loading…</p>;
  else body = <WorkersTable workers={workers} onEdit={openEdit} onDelete={del} onResetPin={resetPin} />;

  return (
    <div className="space-y-6" data-testid="workers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Manage</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">Employees</h1>
          {status?.company?.slug && (
            <p className="text-sm text-slate-600 mt-2">Mobile Company ID: <span className="font-mono font-semibold">{status.company.slug}</span></p>
          )}
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} data-testid="workers-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="workers-import-btn">
            <Upload className="h-4 w-4" /> Import CSV
          </Button>
          <Button onClick={openCreate} className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="workers-add-btn">
            <Plus className="h-4 w-4" /> Add employee
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <p className="px-6 py-3 text-xs text-slate-500 border-b">
          CSV columns: <span className="font-mono">name, employee_code, email, pin</span>. Employee mobile login uses Company ID + Employee ID + PIN.
        </p>
        {body}
      </Card>

      <WorkerFormDialog open={open} onOpenChange={setOpen} form={form} setForm={setForm} onSave={save} isEdit={!!editing} />
    </div>
  );
}
